import React from "react";
import { FiShield, FiLock, FiEye, FiFileText, FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  // Schema.org Structured Data for Legal Service/Company
  const legalSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Shree Construction",
    "description": "Privacy policy and data protection standards for Shree Construction & Services.",
    "publisher": {
      "@type": "Organization",
      "name": "Shree Construction",
      "logo": "https://shree.construction/logo.png"
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900">
      <Helmet>
        <title>Privacy Policy | Shree Construction & Services</title>
        <meta name="description" content="Read the Privacy Policy of Shree Construction. We ensure secure data handling for petroleum projects and industrial construction partnerships." />
        <meta name="robots" content="noindex, follow" /> {/* Privacy pages don't need to compete in search, but should be crawlable */}
        <link rel="canonical" href="https://shree.construction/privacy-policy" />
        <script type="application/ld+json">{JSON.stringify(legalSchema)}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="p-6 bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => navigate("/")}
            className="text-[#0054A6] font-bold flex items-center gap-2 hover:text-[#EE3124] transition-colors"
          >
            <FiArrowLeft /> Back to Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="font-black text-slate-800 uppercase tracking-tighter text-sm">Secure Portal</span>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-16 px-6">
        {/* Header Section */}
        <header className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[#0054A6] rounded-2xl flex items-center justify-center text-white shadow-xl rotate-3">
              <FiShield size={24} />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
              Privacy <span className="text-[#EE3124]">Policy.</span>
            </h1>
          </div>
          <p className="text-slate-500 font-medium">
            Effective Date: May 2026 | Shree Construction & Services (Lucknow Office)
          </p>
        </header>

        {/* Content Card */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-slate-200 shadow-2xl shadow-slate-200/50 space-y-10 text-slate-600 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiEye className="text-[#0054A6]" /> 1. Information Collection & Scope
            </h2>
            <p>
              Shree Construction operates as a specialized contractor for Petroleum Department projects. 
              We collect information to facilitate <strong>B2B partnerships, vendor registrations, and 
              employment applications</strong>. This includes:
            </p>
            <ul className="list-disc ml-6 mt-3 space-y-1 font-medium text-slate-700">
              <li>Identity Data (Name, PAN, GST details)</li>
              <li>Contact Data (Email, Phone, Site Addresses)</li>
              <li>Technical Data (IP addresses and browser types for security monitoring)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiLock className="text-[#0054A6]" /> 2. Institutional Data Handling
            </h2>
            <p>
              Your data is processed under the strict guidelines required for Central Government 
              contracting and petroleum safety standards. We use your data:
            </p>
            <ul className="list-disc ml-6 mt-3 space-y-2">
              <li>To verify vendor credentials for industrial project bidding.</li>
              <li>To maintain on-site attendance and safety logs via our secure dashboard.</li>
              <li>To comply with statutory audits and GST filing requirements.</li>
            </ul>
          </section>

          <section className="bg-slate-50 p-6 rounded-2xl border border-dashed border-slate-300">
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiFileText className="text-[#EE3124]" /> 3. Data Infrastructure & Security
            </h2>
            <p>
              All project logs and personal identifiers are encrypted and stored using 
              <strong> Supabase Professional Grade Infrastructure</strong>. We implement 
              SSL/TLS encryption for all data transmissions between your device and our 
              construction management system.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4">4. Third-Party Disclosure</h2>
            <p>
              We do not sell your data. Information is shared only with <strong>Government 
              Authorities (HPCL/IOCL/BPCL)</strong> or banking partners when strictly necessary 
              for project compliance, billing, or security clearance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4">5. Contact Our Privacy Officer</h2>
            <p>
              For requests regarding data deletion or access to your records, please contact our 
              compliance team at:
            </p>
            <div className="mt-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
              <p className="font-bold text-slate-900">Shree Construction & Services</p>
              <p className="text-sm">Email: shreeconstruction0004@gmail.com</p>
              <p className="text-sm">Location: Lucknow, Uttar Pradesh, India</p>
            </div>
          </section>
        </div>

        <footer className="mt-12 text-center text-slate-400 text-sm">
          © 2026 Shree Construction. All rights reserved. 
          <br />Designed for compliance with Indian IT Act (2000).
        </footer>
      </main>
    </div>
  );
}